// src/hooks/useLiveStreamSnapshot.ts
// Snapshot de l'état opérationnel de la régie dans localStorage.
// Sauvegarde à chaque changement (debounce 300ms), restaure au montage,
// efface quand broadcastingChannel.active passe à false.

import { useEffect, useRef, useCallback } from 'react';
import { SdpLogEntry } from '../dataTypes/liveStreamManagment/types_sdp';
import { WhipNegotiationInfo } from '../dataTypes/liveStreamManagment/types_webrtc';

// ── Clé de stockage ───────────────────────────────────────────────────────────

export const LIVE_STREAM_SNAPSHOT_KEY = 'mediartc_live_stream_snapshot';

/** Période du flush périodique, qui persiste le log SDP hors changement d'état. */
const LOG_FLUSH_MS = 10_000;

// ── Type du snapshot ──────────────────────────────────────────────────────────

export interface LiveStreamSnapshot {
  // Chaîne à laquelle appartient ce snapshot. La clé de stockage est unique pour tout le
  // navigateur : sans ce champ, la régie d'une chaîne restaurait l'état d'une AUTRE (source
  // finale introuvable ici, diffusion « en cours » sur une chaîne éteinte, journal SDP
  // étranger). Un snapshot sans ce champ (ancien format) n'est jamais restauré.
  channelUuid         : string;
  // MediaStreamContext
  selectedVideoSource : string;
  selectedAudio       : string;
  isStreaming         : boolean;
  // ProductionControlRoomContext
  sdpLogRegie         : SdpLogEntry[];
  sdpInitialDevice    : string;
  whipInfo            : WhipNegotiationInfo;
  showSdpDebugRegie   : boolean;
  showDeviceInfoPanel : boolean;
  // Metadata
  savedAt             : number; // Date.now()
}

// ── Helpers bas niveau ────────────────────────────────────────────────────────

export function saveSnapshot(snap: LiveStreamSnapshot): void {
  try {
    localStorage.setItem(LIVE_STREAM_SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {}
}

export function loadSnapshot(): LiveStreamSnapshot | null {
  try {
    const raw = localStorage.getItem(LIVE_STREAM_SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as LiveStreamSnapshot;
  } catch {
    return null;
  }
}

export function clearSnapshot(): void {
  try {
    localStorage.removeItem(LIVE_STREAM_SNAPSHOT_KEY);
  } catch {}
}

// ── Hook principal ────────────────────────────────────────────────────────────

interface UseLiveStreamSnapshotParams {
  channelActive       : boolean;
  /** UUID de la chaîne affichée — null tant qu'il n'est pas résolu. */
  channelUuid         : string | null;

  // État à sauvegarder
  selectedVideoSource : string;
  selectedAudio       : string;
  isStreaming         : boolean;
  /**
   * Lecteur du log SDP, appelé au moment d'écrire. Un getter et non la valeur :
   * le log change des centaines de fois par négociation WebRTC et ne vit
   * volontairement plus dans un state React (cf. ProductionControlRoomContext).
   */
  getSdpLogRegie      : () => SdpLogEntry[];
  sdpInitialDevice    : string;
  whipInfo            : WhipNegotiationInfo;
  showSdpDebugRegie   : boolean;
  showDeviceInfoPanel : boolean;

  // Callback de restauration — appelé une seule fois au montage si snapshot présent
  onRestore: (snap: LiveStreamSnapshot) => void;
}

export function useLiveStreamSnapshot({
  channelActive,
  channelUuid,
  selectedVideoSource,
  selectedAudio,
  isStreaming,
  getSdpLogRegie,
  sdpInitialDevice,
  whipInfo,
  showSdpDebugRegie,
  showDeviceInfoPanel,
  onRestore,
}: UseLiveStreamSnapshotParams): void {

  const restoredRef      = useRef(false);
  const channelActiveRef = useRef(channelActive);
  const onRestoreRef     = useRef(onRestore);
  const getSdpLogRef     = useRef(getSdpLogRegie);

  const channelUuidRef   = useRef(channelUuid);
  useEffect(() => { channelActiveRef.current = channelActive; }, [channelActive]);
  useEffect(() => { channelUuidRef.current   = channelUuid;   }, [channelUuid]);
  useEffect(() => { onRestoreRef.current     = onRestore;     }, [onRestore]);
  useEffect(() => { getSdpLogRef.current     = getSdpLogRegie; }, [getSdpLogRegie]);

  // ── Restauration (une seule fois, dès que la chaîne est identifiée) ─────────
  // L'UUID est souvent encore null au montage : on attend qu'il arrive plutôt que de
  // consommer l'unique tentative à vide. Ne restaure que le snapshot de CETTE chaîne, et
  // seulement si elle est active — le snapshot n'est écrit que chaîne active, un état
  // « en diffusion » n'a aucun sens sur une chaîne éteinte.
  useEffect(() => {
    if (restoredRef.current) return;
    if (!channelUuid) return;
    restoredRef.current = true;

    const snap = loadSnapshot();
    if (snap && channelActive && snap.channelUuid === channelUuid) {
      onRestoreRef.current(snap);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelUuid]);

  // ── Effacement sur front descendant channelActive true → false ───────────────
  const prevActiveRef = useRef(channelActive);
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = channelActive;
    if (wasActive && !channelActive) {
      clearSnapshot();
    }
  }, [channelActive]);

  // ── Sauvegarde continue debouncée à 300ms ────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleWrite = useCallback(() => {
    if (!channelActiveRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!channelActiveRef.current) return;
      if (!channelUuidRef.current) return;
      saveSnapshot({
        channelUuid: channelUuidRef.current,
        selectedVideoSource,
        selectedAudio,
        isStreaming,
        // Lu ici et pas capturé dans les deps : le log n'est plus un state, sa
        // valeur courante est celle de la ref au moment de l'écriture.
        sdpLogRegie: getSdpLogRef.current(),
        sdpInitialDevice,
        whipInfo,
        showSdpDebugRegie,
        showDeviceInfoPanel,
        savedAt: Date.now(),
      });
    }, 300);
  // channelActive géré via ref — intentionnellement absent des deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVideoSource, selectedAudio, isStreaming,
    sdpInitialDevice, whipInfo,
    showSdpDebugRegie, showDeviceInfoPanel,
  ]);

  useEffect(() => {
    scheduleWrite();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [scheduleWrite]);

  // ── Flush périodique ────────────────────────────────────────────────────────
  // Le log SDP ne déclenche plus de rendu, donc plus d'écriture : sans ce
  // battement il ne serait persisté que lorsqu'un AUTRE champ du snapshot change.
  //
  // `scheduleWrite` change d'identité dès qu'un champ du snapshot bouge. Le
  // passer en dépendance de l'effet réarmait l'intervalle à chaque changement :
  // en régie active, il repartait de zéro si souvent qu'il ne tombait
  // pratiquement jamais. Il est donc lu via une ref, et l'intervalle n'est
  // (re)créé que sur bascule de `channelActive`.
  const scheduleWriteRef = useRef(scheduleWrite);
  useEffect(() => { scheduleWriteRef.current = scheduleWrite; }, [scheduleWrite]);

  useEffect(() => {
    if (!channelActive) return;
    const id = setInterval(() => scheduleWriteRef.current(), LOG_FLUSH_MS);
    return () => clearInterval(id);
  }, [channelActive]);
}
